import { createHash, randomUUID } from 'node:crypto';
import { statSync } from 'node:fs';
import { readFile } from 'node:fs/promises';
import { homedir, hostname } from 'node:os';
import path from 'node:path';

import type { Agent } from '@mastra/core/agent';
import { AgentController, migratePersistedModelSelection } from '@mastra/core/agent-controller';
import type {
  IntervalHandler,
  AgentControllerConfig,
  AgentControllerEvent,
  AgentControllerMode,
  AgentControllerSubagent,
  AgentControllerRequestContext,
  BuiltinToolId,
  Session,
} from '@mastra/core/agent-controller';
import { createCodingAgent } from '@mastra/core/coding-agent';
import type { PubSub } from '@mastra/core/events';
import { Knowledge } from '@mastra/core/knowledge';
import { PROVIDER_REGISTRY, findGatewayForModel, getGatewayId } from '@mastra/core/llm';
import type { MastraModelGatewayInterface, ProviderConfig } from '@mastra/core/llm';
import { Mastra } from '@mastra/core/mastra';
import { defaultNotificationDeliveryDecision } from '@mastra/core/notifications';
import {
  AgentsMDInjector,
  createBackgroundWorkSignalProcessor,
  CyberRefusalHandler,
  isBadRequestError,
  ProviderHistoryCompat,
  StreamErrorRetryProcessor,
} from '@mastra/core/processors';
import type { InputProcessor, OutputProcessor, Processor } from '@mastra/core/processors';
import { RequestContext } from '@mastra/core/request-context';
import type { PublicSchema } from '@mastra/core/schema';
import type { ApiRoute } from '@mastra/core/server';
import { TaskSignalProvider } from '@mastra/core/signals';
import type { SignalProvider } from '@mastra/core/signals';
import { MastraCompositeStore } from '@mastra/core/storage';
import { DEFAULT_GOAL_JUDGE_PROMPT } from '@mastra/core/tools';
import type { MastraVector } from '@mastra/core/vector';
import type { DuckDBStore } from '@mastra/duckdb';

import { GithubSignals } from '@mastra/github-signals';
import { LibSQLStore, LibSQLVector } from '@mastra/libsql';
import {
  Observability,
  MastraStorageExporter,
  MastraPlatformExporter,
  SensitiveDataFilter,
} from '@mastra/observability';

import { createSessionThreadAdvertisement } from './agent-connections/session-advertisement.js';
import { AgentConnectionsSignalProvider } from './agent-connections/signal-provider.js';
import type { AgentConnectionsSignalProviderOptions } from './agent-connections/signal-provider.js';
import { createBackgroundCompletionEvents } from './agents/background-completion-events.js';
import { createBackgroundCompletionCallbacks } from './agents/background-completion.js';
import {
  hasCredentialStoreProvider,
  setCredentialLogger,
  withCredentialTenantPreparation,
} from './agents/credential-resolver.js';
import { getDynamicInstructions } from './agents/instructions.js';
import { getDynamicMemory, hasSubconsciousTools } from './agents/memory.js';
import {
  canUseProvider,
  createMastraCodeGateway,
  getDynamicModel,
  getGoalJudgeModel,
  resolveModel,
} from './agents/model.js';
import { buildMode } from './agents/modes/build.js';
import { fastMode } from './agents/modes/explore.js';
import { createPlanMode } from './agents/modes/plan.js';
import {
  createGitRefReminderReader,
  instructionSourcePaths,
  loadSessionInstructions,
  sessionInstructionRoots,
} from './agents/prompts/agent-instructions.js';
import { executeSubagent } from './agents/subagents/execute.js';
import { exploreSubagent } from './agents/subagents/explore.js';
import { planSubagent } from './agents/subagents/plan.js';
import { attachOMThreadStatePersistence, restoreOMThreadStateForCurrentThread } from './agents/thread-caveman-state.js';
import { createDynamicTools, createToolHooks } from './agents/tools.js';
import type { PostToolObserver, ToolLike } from './agents/tools.js';

import { getDynamicWorkspace, getGoalJudgeTools } from './agents/workspace.js';
import {
  AccountRotationProcessor,
  AccountStartNoticeProcessor,
  MODEL_FALLBACK_STATE_KEY,
} from './auth/account-rotation-processor.js';
import { isKimiCodingDeviceId } from './auth/providers/kimi-coding.js';
import { AuthStorage } from './auth/storage.js';
import { DEFAULT_CONFIG_DIR, DEFAULT_OM_MODEL_ID, validateConfigDirName } from './constants.js';
import { createOutcomeScorer, createEfficiencyScorer } from './evals/scorers/index.js';
import { resolveExperimentalAgent, validateExperimentalAgent, wrapExperimentalAgent } from './experimental-agent.js';
import { HookManager } from './hooks/index.js';
import { createKnowledgeInspector as createScopedKnowledgeInspector } from './knowledge-inspector.js';
import { createMcpManager } from './mcp/index.js';
import type { McpServerConfig } from './mcp/index.js';
import type { ProviderAccess } from './onboarding/packs.js';
import { getAvailableOmPacks, resolveAutoOMModelId } from './onboarding/packs.js';
import {
  loadSettings,
  MASTRA_GATEWAY_PROVIDER,
  OBSERVABILITY_AUTH_PREFIX,
  resolveOmRoleModel,
  saveSettings,
} from './onboarding/settings.js';
import { getToolCategory } from './permissions.js';
import { PluginManager } from './plugins/manager.js';
import { PluginSignalLane } from './plugins/signal-lane.js';
import type { PluginProcessorEntries } from './plugins/types.js';
import { PlanRejectionAbortProcessor } from './processors/plan-rejection-abort.js';
import { createAmazonBedrockGateway } from './providers/amazon-bedrock-gateway.js';
import { setAuthStorage } from './providers/claude-max.js';
import { createGitHubCopilotGateway } from './providers/github-copilot-gateway.js';
import { setAuthStorage as setGitHubCopilotAuthStorage } from './providers/github-copilot.js';
import { setAuthStorage as setKimiCodingAuthStorage } from './providers/kimi-coding.js';
import { setAuthStorage as setOpenAIAuthStorage } from './providers/openai-codex.js';
import { setAuthStorage as setXAIAuthStorage } from './providers/xai.js';

import {
  assembleSchedulePrompt,
  createScheduleTools,
  runScript,
  scheduleSignalAttributes,
  shortScheduleId,
  ThreadScheduler,
} from './schedules/index.js';
import { stateSchema } from './schema.js';
import type { MastraCodeState } from './schema.js';

import { mastraBrand } from './theme-palette.js';
import { isThinkingLevelSetting } from './thinking.js';
import type { ThinkingLevelSetting } from './thinking.js';
import { DiscardingScoresStorage } from './utils/discarding-scores-storage.js';
import { syncGateways } from './utils/gateway-sync.js';
import { openLocalObservabilityStore } from './utils/local-observability.js';
import { registerSessionAndWaitForMaintenance, UNKNOWN_OWNER, unregisterSession } from './utils/maintenance-lock.js';
import { createResourceNotificationDispatcher, shouldHoldNotificationDelivery } from './utils/notification-dispatch.js';
import {
  detectProject,
  getObservabilityDatabasePath,
  getStorageConfig,
  getResourceIdOverride,
} from './utils/project.js';
import type { StorageConfig } from './utils/project.js';
import { createSignalsPubSub } from './utils/signals-pubsub.js';
import { createStorage, createVectorStore } from './utils/storage-factory.js';
import type { StorageResult } from './utils/storage-factory.js';
import { createStorageMaintenance, DEFAULT_RETENTION, resolveLocalDbFiles } from './utils/storage-maintenance.js';
import type { StorageMaintenance } from './utils/storage-maintenance.js';
import { acquireThreadLock, releaseThreadLock } from './utils/thread-lock.js';
import { FOREIGN_THREAD, findWakeSession, sessionLeaseScopes } from './utils/wake-session.js';
import type { WakeSession } from './utils/wake-session.js';
import { registerWorkflowBuilderPrimitives } from './workflows/register-primitives.js';

const CODE_AGENT_ID = 'code-agent';

// Global retry policy for transient provider failures (e.g. dropped sockets and server errors).
// Applied centrally to every model call via StreamErrorRetryProcessor, independent of model-pack
// settings, so all modes/subagents benefit from a short wait before retrying a transient failure.
// Delay uses exponential backoff: initialDelay * 2^retryCount, capped at maxDelay.
const MASTRACODE_TRANSIENT_CONNECTION_MAX_RETRIES = 10;
// Shared ceiling for the whole processor error lane: core counts every
// processor-requested retry against this one number. Sized for the largest
// legitimate cascade — a rotation step per remaining account per pack hop
// (8 representative accounts x 8 packs) — rather than an open-ended budget, so
// a custom or plugin processor that returns `retry: true` loops at most this
// many times instead of amplifying one request without bound.
const MASTRACODE_MAX_PROCESSOR_RETRIES = 64;
const MASTRACODE_TRANSIENT_CONNECTION_RETRY_INITIAL_DELAY_MS = 500;
const MASTRACODE_TRANSIENT_CONNECTION_RETRY_MAX_DELAY_MS = 30000;

const TRANSIENT_CONNECTION_ERROR_CODES = new Set(['ECONNRESET', 'EPIPE']);
const TRANSIENT_CONNECTION_MESSAGE_PATTERN = /econnreset|socket hang up|write epipe|other side closed/i;
// Any 5xx is treated as transient so server errors exhaust the transient retry
// budget before the account-rotation processor classifies them as a persistent
// outage (hop). Must stay aligned with `classifyRotationError`'s 5xx check.
const isTransientServerStatus = (status: number) => status >= 500 && status < 600;
const TRANSIENT_SERVER_ERROR_MESSAGE_PATTERN = /internal server|server error|api may be experiencing issues/i;

/**
 * Matcher for transient connection failures. Cause-chain traversal is handled
 * by `StreamErrorRetryProcessor.isRetryableStreamError`, which calls each
 * matcher at every level of the cause chain.
 */
/**
 * Read the session state fields the AgentsMDInjector callbacks need from the
 * controller request context (set by hosts like the factory review flow).
 */
function getInjectorSessionState(
  requestContext: { get: (key: string) => unknown } | undefined,
): { untrustedCheckout?: boolean; baseRef?: string; projectPath?: string; instructionRoots?: string[] } | undefined {
  const agentControllerContext = requestContext?.get('controller') as
    | AgentControllerRequestContext<{
        untrustedCheckout?: boolean;
        baseRef?: string;
        projectPath?: string;
        instructionRoots?: string[];
      }>
    | undefined;
  return agentControllerContext?.getState();
}

function isTransientConnectionError(error: unknown): boolean {
  if (!error) return false;

  const code = typeof error === 'object' && 'code' in error ? (error as { code?: unknown }).code : undefined;
  if (typeof code === 'string' && TRANSIENT_CONNECTION_ERROR_CODES.has(code.toUpperCase())) return true;

  const message = error instanceof Error ? error.message : undefined;
  if (typeof message === 'string' && TRANSIENT_CONNECTION_MESSAGE_PATTERN.test(message)) return true;

  return false;
}

function isTransientServerError(error: unknown): boolean {
  if (!error) return false;

  const errorObj = typeof error === 'object' ? (error as { status?: unknown; statusCode?: unknown }) : undefined;
  if (
    (typeof errorObj?.status === 'number' && isTransientServerStatus(errorObj.status)) ||
    (typeof errorObj?.statusCode === 'number' && isTransientServerStatus(errorObj.statusCode))
  ) {
    return true;
  }

  const message = error instanceof Error ? error.message : undefined;
  return typeof message === 'string' && TRANSIENT_SERVER_ERROR_MESSAGE_PATTERN.test(message);
}

function getTransientRetryDelay(retryCount: number): number {
  return Math.min(
    MASTRACODE_TRANSIENT_CONNECTION_RETRY_INITIAL_DELAY_MS * Math.pow(2, retryCount),
    MASTRACODE_TRANSIENT_CONNECTION_RETRY_MAX_DELAY_MS,
  );
}

function emitTransientRetry(
  error: unknown,
  retryCount: number,
  delayMs: number,
  requestContext?: RequestContext,
): void {
  const controllerContext = requestContext?.get('controller') as AgentControllerRequestContext | undefined;
  controllerContext?.emitEvent?.({
    type: 'error',
    error: error instanceof Error ? error : new Error(String(error)),
    retryable: true,
    retryDelay: delayMs,
    retryAttempt: retryCount + 1,
    maxRetries: MASTRACODE_TRANSIENT_CONNECTION_MAX_RETRIES,
  });
}

/** Short deterministic hash (sha256, first 12 hex chars) matching project.ts shortHash style. */
function shortHash(input: string): string {
  return createHash('sha256').update(input).digest('hex').slice(0, 12);
}

function applyModeDefaultsToModes(
  modes: AgentControllerMode[],
  modeDefaults: Record<string, string>,
): AgentControllerMode[] {
  return modes.map(mode => {
    const savedModel = modeDefaults[mode.id];
    if (!savedModel) {
      return mode;
    }
    return {
      ...mode,
      defaultModelId: savedModel,
    };
  });
}

function addPluginToolsToModeAllowlists(
  modes: AgentControllerMode[],
  pluginToolNames: string[],
): AgentControllerMode[] {
  if (pluginToolNames.length === 0) return modes;
  return modes.map(mode => {
    if (!mode.availableTools) return mode;
    return {
      ...mode,
      availableTools: Array.from(new Set([...mode.availableTools, ...pluginToolNames])),
    };
  });
}

export interface MastraCodeConfig {
  /** Working directory for project detection. Default: process.cwd() */
  cwd?: string;
  /** Home directory for global config discovery. Default: os.homedir() */
  homeDir?: string;
  /**
   * Override modes (model IDs, colors, which modes exist). Default: build/plan/fast.
   * `false` runs every session as the single Build agent with no mode switching:
   * threads saved in another mode reopen as Build, and per-mode thinking defaults never apply.
   */
  modes?: AgentControllerMode[] | false;
  /** Replace subagent definitions. Default: explore/plan/execute. An empty array disables subagents. */
  subagents?: AgentControllerSubagent[];
  /** Extra tools merged into the dynamic tool set. Can be a static record or a (sync or async) function that receives requestContext. */
  extraTools?:
    | Record<string, ToolLike | undefined>
    | ((ctx: {
        requestContext: RequestContext;
      }) => Record<string, ToolLike | undefined> | Promise<Record<string, ToolLike | undefined>>);
  /** Observe completed tool calls without replacing or modifying the built-in tool implementation. */
  postToolObserver?: PostToolObserver;
  /**
   * Stateless input processor instances prepended before Mastra Code's mandatory processors,
   * or a per-request resolver for processors that depend on trusted request context.
   * Embedders may extend processing but cannot replace built-in safety and compatibility policy.
   */
  inputProcessors?:
    | InputProcessor[]
    | ((args: { requestContext: RequestContext }) => InputProcessor[] | Promise<InputProcessor[]>);
  /**
   * Output processor instances appended after Mastra Code's built-in output processors,
   * or a per-request resolver. They run before the memory layers the Agent appends,
   * so changes they make to response messages are persisted.
   */
  outputProcessors?:
    | OutputProcessor[]
    | ((args: { requestContext: RequestContext }) => OutputProcessor[] | Promise<OutputProcessor[]>);
  /** Tools removed from the dynamic tool set before exposure to the model */
  disabledTools?: string[];
  /**
   * Agent controller built-in tools (e.g. `submit_plan`, `ask_user`) removed from
   * every session. Unlike `disabledTools`, which filters Mastra Code's dynamic
   * tools, this is forwarded to the AgentController. Prompt guidance for a
   * disabled built-in is omitted so the model is never told to call it.
   */
  disableBuiltinTools?: BuiltinToolId[];
  /**
   * Custom storage config instead of auto-detected default, or a pre-built
   * store instance. An instance is used as-is: no connection test and no
   * LibSQL fallback — if the injected store fails, that's a hard error.
   */
  storage?: StorageConfig | MastraCompositeStore;
  /** Backend for an injected custom storage instance. Inferred for LibSQLStore and PostgresStore. */
  storageBackend?: 'libsql' | 'pg';
  /** Pre-built vector store instance for recall search. Skips the default vector store creation. */
  vector?: MastraVector;
  /** Host-owned Knowledge instance and the key used to register it on the mounted Mastra runtime. */
  knowledge?: { key: string; instance: Knowledge };
  /** Observational memory scope. Default: auto-detected from env/config files, falls back to 'thread' */
  omScope?: 'thread' | 'resource';
  /** Path to a custom settings.json file. Default: global settings */
  settingsPath?: string;
  /** Initial state overrides (yolo, thinkingLevel, etc.) */
  initialState?: Partial<MastraCodeState>;
  /**
   * Host-owned thinking default for a request without its own level. When set,
   * it replaces the settings.json defaults (`preferences.thinkingLevel` and the
   * per-mode defaults); a session's own level still wins. Called on every model
   * resolution, so a changed default applies to the next request.
   */
  resolveDefaultThinkingLevel?: (ctx: {
    requestContext: RequestContext;
  }) => ThinkingLevelSetting | Promise<ThinkingLevelSetting>;
  /** Create a thread during local boot when no existing thread matches. Default: true */
  createInitialThread?: boolean;
  /** Trusted host instructions resolved outside mutable session state. */
  hostInstructions?:
    | string
    | ((ctx: { requestContext: RequestContext }) => string | undefined | Promise<string | undefined>);
  /** Commit co-author identity included in coding-agent commit guidance. Unspecified fields use core defaults. */
  coAuthor?: { name?: string; email?: string };
  /** Override id generation for threads/messages. Primarily useful for deterministic tests. */
  idGenerator?: AgentControllerConfig<MastraCodeState>['idGenerator'];
  /** Override interval handlers. Default: gateway-sync */
  intervalHandlers?: IntervalHandler[];
  /** Override the workspace. Default: local filesystem + local sandbox based on detected project */
  workspace?: AgentControllerConfig<MastraCodeState>['workspace'];
  /** Override the config directory name. Default: '.mastracode'. Replaces '.mastracode' in all project-level and global config paths (MCP, hooks, commands, database, skills, agent instructions). */
  configDir?: string;
  /** Programmatic MCP server configurations, merged with (and overriding) file-based configs. */
  mcpServers?: Record<string, McpServerConfig>;
  /** Disable MCP server discovery. Default: false */
  disableMcp?: boolean;
  /** Disable hooks. Default: false */
  disableHooks?: boolean;
  /** Skip loading cwd/.env into the process environment. Useful for multi-session hosts. */
  disableEnvFile?: boolean;
  /** Disable plugin discovery/loading. Default: false */
  disablePlugins?: boolean;
  /** Disable the polling-based GitHub signal provider even when enabled in global settings. Default: false */
  disableGithubSignals?: boolean;
  /**
   * Skip seeding observational-memory knobs (observer/reflector models,
   * thresholds, caveman mode, attachment observation) from settings.json.
   * Server deployments that persist memory settings in their own database
   * (the factory's `memory-settings` domain) set this so the host machine's
   * TUI settings file never leaks into server sessions. Default: false.
   */
  disableSettingsOmSeed?: boolean;
  /** Override the plugin manager. Primarily useful for tests or embedding. */
  pluginManager?: PluginManager;
  /**
   * Override the memory instance (or dynamic factory) passed to the AgentController.
   * When provided, this replaces the default `getDynamicMemory(storage, vector)` which
   * uses mastracode's built-in model gateway (Anthropic OAuth, OpenAI Codex,
   * custom providers, and models.dev fallback).
   *
   * Use this when you need to override memory model behavior completely.
   */
  memory?: AgentControllerConfig<MastraCodeState>['memory'] | false;
  /** Browser provider for browser automation tools. When set, the agent gains access to browser tools. */
  browser?: AgentControllerConfig<MastraCodeState>['browser'];
  /** PubSub for signal routing. When crossProcessPubSub is true, thread locks are disabled. */
  pubsub?: PubSub;
  /** Use Mastra Code's built-in Unix socket PubSub for local cross-process signal routing. */
  unixSocketPubSub?: boolean;
  /** Marks the configured PubSub as cross-process-safe, allowing Mastra Code to skip file thread locks. */
  crossProcessPubSub?: boolean;
  /** Agent connection state and discovery options. */
  agentConnections?: AgentConnectionsSignalProviderOptions;
  /**
   * Enable experimental cross-agent communication: thread ownership
   * advertisement, peer discovery, and the agent connection tools. Defaults to
   * the `signals.experimentalCrossAgentSignals` global setting (off). With the
   * built-in Unix socket PubSub (`unixSocketPubSub`, no `pubsub` injected),
   * peer discovery also reaches agents in other projects on this machine.
   */
  crossAgentSignals?: boolean;
  /**
   * Enable the experimental agent schedule tools (`schedule_create`,
   * `schedule_list`, `schedule_update`, `schedule_run`), which manage the same
   * process-local schedules as `/schedules`. Defaults to the
   * `signals.experimentalScheduleTools` global setting (off).
   */
  scheduleTools?: boolean;
  /**
   * Prepare the request context of a wake: a run on a thread with no inbound
   * request, such as a notification or cross-agent signal delivered to an idle
   * thread. Hosts that resolve credentials per tenant use this to attach the
   * identity that owns `resourceId` before the run starts. Called only when a
   * session owns `resourceId`, whenever Mastra Code builds a wake's stream
   * options.
   */
  prepareWakeRequestContext?: (args: {
    requestContext: RequestContext;
    resourceId: string;
    threadId: string;
  }) => void | Promise<void>;
  /**
   * Bring the session that owns `resourceId` online when a notification is
   * delivered and no session in this process owns it (a deferred delivery
   * after the session closed, a restart, another replica). Without it such a
   * delivery wakes the thread with no request context: no owner identity, no
   * model selection. Create the session as its owner, or throw when the owner
   * can't be resolved. When set, a delivery that still finds no owning session
   * fails with that error instead of running.
   */
  resumeWakeSession?: (args: { resourceId: string; threadId: string }) => Promise<void>;
}

export function createAuthStorage() {
  const authStorage = new AuthStorage();
  setAuthStorage(authStorage);
  setOpenAIAuthStorage(authStorage);
  setGitHubCopilotAuthStorage(authStorage);
  setKimiCodingAuthStorage(authStorage);
  setXAIAuthStorage(authStorage);
  return authStorage;
}

/**
 * Resolve cloud observability credentials for the MastraPlatformExporter.
 * Priority: per-resource settings > MASTRACODE_* environment variables > undefined (no exporter).
 *
 * The env vars are deliberately namespaced `MASTRACODE_*`, not `MASTRA_*`: the
 * cwd `.env` is loaded into `process.env`, so reading `MASTRA_PROJECT_ID` /
 * `MASTRA_CLOUD_ACCESS_TOKEN` would export Mastra Code's own traces into
 * whatever Mastra project the user happens to be working in (and crash on
 * project ids the exporter rejects).
 */
export function resolveCloudObservabilityConfig(
  settings: ReturnType<typeof loadSettings>,
  authStorage: AuthStorage,
  resourceId: string,
  env: NodeJS.ProcessEnv = process.env,
): { accessToken: string; projectId?: string } | undefined {
  const resourceConfig = settings.observability.resources[resourceId];
  if (resourceConfig) {
    const token = authStorage.getStoredApiKey(`${OBSERVABILITY_AUTH_PREFIX}${resourceId}`);
    if (token) {
      return { accessToken: token, projectId: resourceConfig.projectId };
    }
  }
  const accessToken = env.MASTRACODE_CLOUD_ACCESS_TOKEN?.trim();
  if (!accessToken) return undefined;
  return { accessToken, projectId: env.MASTRACODE_PROJECT_ID?.trim() || undefined };
}

/**
 * Base factory: builds every shared MastraCode resource (storage, observability,
 * memory, MCP, providers, gateways, agent, modes) and the {@link AgentController}, but
 * does NOT call `init()` or create a session. The controller is returned inert so
 * the composition layer can decide its Mastra ownership and session model.
 *
 * See {@link bootLocalAgentController} (Case 3) and `mountAgentControllerOnMastra` (Cases 1 & 2).
 */
/**
 * `instanceof` checks against Mastra classes are unreliable here: published
 * packages pin exact `@mastra/core` versions, so a user's dependency graph can
 * contain multiple copies of core (and peer-keyed copies of `@mastra/libsql` /
 * `@mastra/pg`). A store built against one copy fails `instanceof` against
 * another — the injected instance then silently fell through to the
 * StorageConfig path and crashed on `config.url`. These structural checks work
 * across duplicated copies.
 */
function isInjectedStorageInstance(storage: MastraCodeConfig['storage']): storage is MastraCompositeStore {
  if (!storage) return false;
  if (storage instanceof MastraCompositeStore) return true;
  // A StorageConfig is a plain data object with a string `backend`
  // discriminant; a store instance carries the MastraCompositeStore method
  // surface.
  const candidate = storage as Partial<MastraCompositeStore>;
  return typeof candidate.init === 'function' && typeof candidate.__registerMastra === 'function';
}

/** Cross-copy-safe class check: walks the prototype chain by constructor name. */
function hasAncestorClassNamed(value: object, className: string): boolean {
  for (let proto = Object.getPrototypeOf(value); proto; proto = Object.getPrototypeOf(proto)) {
    if (proto.constructor?.name === className) return true;
  }
  return false;
}

function resolveInjectedStorageBackend(
  storage: MastraCompositeStore,
  configuredBackend?: 'libsql' | 'pg',
): 'libsql' | 'pg' {
  if (configuredBackend) return configuredBackend;
  if (storage instanceof LibSQLStore || hasAncestorClassNamed(storage, 'LibSQLStore')) return 'libsql';
  // By name only: an `instanceof` check would load `@mastra/pg` for every libsql session.
  if (hasAncestorClassNamed(storage, 'PostgresStore')) return 'pg';
  throw new Error('storageBackend is required when injecting a custom storage instance.');
}

/** Whether the model router would resolve `modelId` through a gateway outside `sdkGatewayIds`. */
function routesToOtherGateway(
  modelId: string,
  gateways: MastraModelGatewayInterface[],
  sdkGatewayIds: ReadonlySet<string>,
): boolean {
  try {
    return !sdkGatewayIds.has(getGatewayId(findGatewayForModel(modelId, gateways)));
  } catch (error) {
    // No gateway at all falls back to the SDK resolver; any other lookup
    // failure is the gateway's to report, not a reason to switch providers.
    if ((error as { id?: string })?.id === 'MODEL_ROUTER_NO_GATEWAY_FOUND') return false;
    throw error;
  }
}

export async function createMastraCodeAgentController(config?: MastraCodeConfig) {
  const cwd = config?.cwd ?? process.cwd();
  const homeDir = config?.homeDir ?? config?.initialState?.homeDir;
  const configDir = config?.configDir ?? DEFAULT_CONFIG_DIR;
  const modeless = config?.modes === false;
  // The single session for this process, assigned once `createSession()` runs
  // below. Config callbacks defined before then (e.g. notification stream
  // options) read it lazily through this holder.
  let activeSession: Session<MastraCodeState> | undefined;
  // Every session this controller has live, whose resources this process
  // dispatches notifications for.
  const liveSessions = new Set<Session<MastraCodeState>>();
  const sessionRouter = {
    getSession: (sessionId: string) => controller.getSession(sessionId),
    listLiveSessions: () => liveSessions,
  };
  // Same trick for the controller, which plugins reach through a lazy accessor.
  // Plugins load well before the controller is constructed, and a closure over
  // the `controller` binding itself would throw on early access rather than
  // reporting "not ready yet", so the accessor reads this holder instead.
  let pluginRuntimeController: AgentController<MastraCodeState> | undefined;
  if (configDir !== DEFAULT_CONFIG_DIR) {
    validateConfigDirName(configDir);
  }

  // Load .env file from cwd if present (for observability API keys, etc.)
  if (!config?.disableEnvFile) {
    try {
      process.loadEnvFile(path.join(cwd, '.env'));
    } catch {
      // No .env file — that's fine, keys may be in shell environment
    }
  }

  // Auth storage (shared with Claude Max / OpenAI providers and AgentController)
  const authStorage = createAuthStorage();
  const globalSettings = loadSettings(config?.settingsPath);
  const experimentalAgent = resolveExperimentalAgent(globalSettings);
  const backgroundToolsEnabled = globalSettings.backgroundTools?.enabled ?? false;
  const storedGatewayKey = authStorage.getStoredApiKey(MASTRA_GATEWAY_PROVIDER);
  const storedGatewayUrl = globalSettings.memoryGateway?.baseUrl;

  if (storedGatewayKey) {
    process.env['MASTRA_GATEWAY_API_KEY'] ??= storedGatewayKey;
  }

  if (storedGatewayUrl) {
    process.env['MASTRA_GATEWAY_URL'] ??= storedGatewayUrl;
  }

  // Load user-entered API keys from auth.json into process.env
  // (only sets env vars that aren't already present — env vars take precedence).
  // Skipped in deployed multi-tenant mode: when a per-tenant credential store
  // provider is registered, provider keys are resolved per request and must
  // never leak into process-global env vars.
  if (!hasCredentialStoreProvider()) {
    try {
      const registry = PROVIDER_REGISTRY as Record<string, ProviderConfig>;
      const providerEnvVars: Record<string, string | undefined> = {};
      for (const [provider, cfg] of Object.entries(registry)) {
        const envVars = cfg?.apiKeyEnvVar;
        providerEnvVars[provider] = Array.isArray(envVars) ? envVars[0] : envVars;
      }
      providerEnvVars[MASTRA_GATEWAY_PROVIDER] ??= 'MASTRA_GATEWAY_API_KEY';
      authStorage.loadStoredApiKeysIntoEnv(providerEnvVars);
    } catch {
      // Registry unavailable — load well-known provider keys so non-gateway flows still work
      authStorage.loadStoredApiKeysIntoEnv({
        [MASTRA_GATEWAY_PROVIDER]: 'MASTRA_GATEWAY_API_KEY',
        anthropic: 'ANTHROPIC_API_KEY',
        openai: 'OPENAI_API_KEY',
        google: 'GOOGLE_GENERATIVE_AI_API_KEY',
        cerebras: 'CEREBRAS_API_KEY',
        deepseek: 'DEEPSEEK_API_KEY',
      });
    }
  }

  const mgApiKey = process.env['MASTRA_GATEWAY_API_KEY'] ?? storedGatewayKey;
  const mastraGatewayBaseUrl = (
    process.env['MASTRA_GATEWAY_URL'] ??
    storedGatewayUrl ??
    'https://gateway-api.mastra.ai'
  )
    .replace(/\/+$/, '')
    .replace(/\/v1$/, '');
  const mastraCodeGateway = createMastraCodeGateway({
    mastraGatewayBaseUrl,
    mastraGatewayApiKey: mgApiKey,
    routeThroughMastraGateway: false,
    settingsPath: config?.settingsPath,
  });
  const amazonBedrockGateway = createAmazonBedrockGateway();
  const githubCopilotGateway = createGitHubCopilotGateway();

  // Project detection
  const project = detectProject(cwd);

  const resourceIdOverride = getResourceIdOverride(project.rootPath, configDir);
  if (resourceIdOverride) {
    project.resourceId = resourceIdOverride;
    project.resourceIdOverride = true;
  }

  const cloudObservabilityConfig = resolveCloudObservabilityConfig(globalSettings, authStorage, project.resourceId);

  // Stable session id unique to this project/resource. Mastra Code has no
  // authenticated user identity in settings, so local sessions use a stable
  // machine-and-project host identity. Core stamps it onto new threads as the
  // ownerId/createdBy fallback; it is not a user id.
  const sessionId = `mastracode-session-${shortHash(project.resourceId)}`;
  const ownerId = `mastracode-${shortHash(`${hostname()}\0${project.rootPath}`)}`;

  // Cross-agent communication is experimental and opt-in. It gates the agent
  // connections provider/tools and the session thread-ownership lifecycle.
  // On the built-in PubSub it also makes peer discovery reach other projects.
  const useCrossAgentSignals =
    config?.crossAgentSignals ?? globalSettings.signals?.experimentalCrossAgentSignals ?? false;

  const configuredPubSub = config?.pubsub;
  const useUnixSocketPubSub =
    (config?.unixSocketPubSub ?? globalSettings.signals?.unixSocketPubSub ?? false) && process.platform !== 'win32';
  const ownSignalsPubSub =
    !configuredPubSub && useUnixSocketPubSub
      ? createSignalsPubSub(project.resourceId, { sharedAgentDiscovery: useCrossAgentSignals })
      : undefined;
  const signalsPubSub = configuredPubSub ?? ownSignalsPubSub;
  const crossProcessPubSub = config?.crossProcessPubSub ?? Boolean(ownSignalsPubSub);
  if (crossProcessPubSub && !signalsPubSub) {
    throw new Error('crossProcessPubSub requires a pubsub instance');
  }
  const useScheduleTools = config?.scheduleTools ?? globalSettings.signals?.experimentalScheduleTools ?? false;

  // Storage. An injected instance is used as-is — no connection test, no
  // LibSQL fallback: if the injected store fails, that's a hard error.
  const injectedStorage = isInjectedStorageInstance(config?.storage) ? config.storage : undefined;
  const storageConfig = injectedStorage
    ? undefined
    : ((config?.storage as StorageConfig | undefined) ??
      getStorageConfig(project.rootPath, globalSettings.storage, configDir));
  if (!injectedStorage) {
    // Register first, then check the maintenance lock, so `mastracode prune`
    // (lock first, then sessions) can never run against an open session.
    await registerSessionAndWaitForMaintenance({
      onWait: pid =>
        console.error(
          `Waiting for storage maintenance (mastracode prune${pid === UNKNOWN_OWNER ? '' : `, PID ${pid}`}) to finish. Press Ctrl+C to give up.`,
        ),
    });
  }
  let storageResult: StorageResult;
  if (injectedStorage) {
    storageResult = {
      storage: injectedStorage,
      backend: resolveInjectedStorageBackend(injectedStorage, config?.storageBackend),
    };
  } else {
    try {
      storageResult = await createStorage(storageConfig!);
    } catch (error) {
      // A still-alive process that failed to open storage must not block prune.
      unregisterSession();
      throw error;
    }
  }
  const storageWarning = storageResult.warning;

  // Observability storage (DuckDB — separate file for OLAP-style trace/score/feedback queries).
  // Local tracing is opt-in via `/observability local on`. When disabled, the
  // MastraStorageExporter is omitted entirely so traces never fall through to
  // the default libsql backend.
  let observabilityDomain: DuckDBStore['observability'] | undefined;
  let observabilityWarning: string | undefined;
  if (globalSettings.observability.localTracing) {
    try {
      const { store: observabilityDuckDB } = await openLocalObservabilityStore(getObservabilityDatabasePath());
      observabilityDomain = observabilityDuckDB.observability;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const isLockError = /lock|locked|busy/i.test(message);
      if (isLockError) {
        observabilityWarning =
          'Observability unavailable — another MastraCode instance holds the database lock. Traces, scores, and feedback will not be recorded in this session.';
      } else {
        observabilityWarning = `Observability unavailable — DuckDB initialization failed: ${message}`;
      }
    }
  }

  // mastracode's scorers persist every result through the scores domain, but
  // nothing reads scores back. Accept the writes and keep nothing, so they
  // neither grow mastra.db nor the process heap.
  const scoresStorage = new DiscardingScoresStorage();

  const storage = new MastraCompositeStore({
    id: 'mastra-code-storage',
    default: storageResult.storage,
    domains: {
      // When local tracing is off, disable the observability domain entirely so
      // trace/score/feedback writes never fall through to the default libsql store.
      observability: observabilityDomain ?? false,
      scores: scoresStorage,
    },
  });

  // Observability (tracing, scoring, feedback)
  const observability = new Observability({
    configs: {
      default: {
        serviceName: 'mastracode',
        // Only these requestContext keys are stored on spans — prevents leaking
        // large objects (controller state, workspace, env vars) into trace data.
        // Use dot-notation because these are nested inside the 'controller' key.
        //
        // Session identifiers:
        //   threadId, resourceId, session.modeId, agentControllerId
        // Environment & project:
        //   state.projectName, state.gitBranch
        // Model configuration:
        //   session.modelId, state.subagentModelId
        // Agent settings:
        //   state.yolo, state.thinkingLevel, state.smartEditing
        // Observational memory settings:
        //   state.omScope, role selection intent/effective model,
        //   state.observationThreshold, state.reflectionThreshold
        requestContextKeys: [
          // Session identifiers
          'controller.threadId',
          'controller.resourceId',
          'controller.session.modeId',
          'controller.controllerId',
          // Environment & project
          'controller.state.projectName',
          'controller.state.gitBranch',
          // Model configuration
          'controller.session.modelId',
          'controller.state.subagentModelId',
          // Agent settings
          'controller.state.yolo',
          'controller.state.thinkingLevel',
          'controller.state.smartEditing',
          // Observational memory settings
          'controller.state.omScope',
          'om.observer.selectionMode',
          'om.observer.effectiveModelId',
          'om.reflector.selectionMode',
          'om.reflector.effectiveModelId',
          'controller.state.observationThreshold',
          'controller.state.reflectionThreshold',
        ],
        exporters: [
          // Only persist traces locally when DuckDB observability is available
          // (via `/observability local on`). Without this guard the storage
          // exporter falls through to the default libsql backend and silently
          // fills the main database with gigabytes of span data.
          ...(observabilityDomain ? [new MastraStorageExporter({ strategy: 'event-sourced' })] : []),
          // Credentials are always passed explicitly; `resolveFromEnv: false`
          // stops the exporter from picking up the cwd project's MASTRA_* vars.
          new MastraPlatformExporter({ ...cloudObservabilityConfig, resolveFromEnv: false }),
        ],
        spanOutputProcessors: [new SensitiveDataFilter()],
      },
    },
  });

  // Vector store for recall search (separate DB file to avoid bloating main
  // storage). An injected instance is used as-is; with an injected storage
  // instance and no injected vector, recall search stays vector-less.
  const vector =
    config?.vector ?? (storageConfig ? await createVectorStore(storageConfig, storageResult.backend) : undefined);

  // Maintenance handle for /prune: prunes via the inner store (whose retention
  // config covers every domain, including legacy libsql observability spans)
  // and can compact local libsql files to reclaim disk. The vector store's
  // connection must close alongside storage — the compaction's file swap
  // refuses to run while any connection is open.
  const storageMaintenance: StorageMaintenance = createStorageMaintenance({
    storage: storageResult.storage,
    backend: storageResult.backend,
    retention: DEFAULT_RETENTION,
    localDbFiles: storageConfig ? resolveLocalDbFiles(storageConfig, storageResult.backend) : [],
    closeVector: vector instanceof LibSQLVector ? () => vector.close() : undefined,
  });

  const configuredKnowledgeKey = config?.knowledge?.key.trim();
  if (config?.knowledge && !configuredKnowledgeKey) {
    throw new Error('knowledge.key must be a non-empty string.');
  }
  const knowledge =
    config?.knowledge?.instance ??
    (process.env.MASTRACODE_EXPERIMENTAL_SUBCONSCIOUS === '1'
      ? new Knowledge({ id: 'mastracode', name: 'MastraCode Knowledge', storage })
      : undefined);
  const knowledgeKey = configuredKnowledgeKey ?? 'default';
  const dynamicMemory =
    config?.memory === undefined
      ? getDynamicMemory(
          storage,
          vector,
          config?.settingsPath,
          {
            disableSettingsOmSeed: config?.disableSettingsOmSeed,
          },
          knowledge,
        )
      : undefined;
  const memory =
    config?.memory === false
      ? undefined
      : (config?.memory ??
        // Keep settled() so shutdown still drains background memory work.
        (dynamicMemory &&
          Object.assign(withCredentialTenantPreparation(dynamicMemory), { settled: dynamicMemory.settled })));
  // Only the default memory wiring registers the subconscious tools; a
  // caller-supplied memory is opaque here, so its prompt must not advertise them.
  const hasSubconscious =
    config?.memory === undefined ? (state: MastraCodeState | undefined) => hasSubconsciousTools(vector, state) : false;

  // MCP
  const mcpManager = config?.disableMcp
    ? undefined
    : createMcpManager(project.rootPath, configDir, config?.mcpServers, globalSettings.mcp);

  // Hooks
  const hookManager = config?.disableHooks
    ? undefined
    : new HookManager(
        project.rootPath,
        'session-init',
        configDir,
        homeDir,
        project.isWorktree
          ? { path: project.rootPath, branch: project.gitBranch, mainRepoPath: project.mainRepoPath }
          : undefined,
      );

  const pluginManager = config?.disablePlugins
    ? undefined
    : (config?.pluginManager ??
      new PluginManager({
        projectRoot: project.rootPath,
        configDir,
        homeDir,
      }));
  // Publish the runtime accessors to whichever manager is in play — including an
  // injected one, which would otherwise hand plugins `undefined` for
  // `getController`/`getActiveSession`. Lazy closures: both locals are assigned
  // after the controller is constructed below.
  pluginManager?.setRuntime({
    getController: () => pluginRuntimeController,
    getActiveSession: () => activeSession,
    getStorage: () => ({ storage, storageBackend: storageResult.backend, vector }),
  });
  const loadedPlugins = pluginManager ? await pluginManager.reload() : [];
  const pluginTools = pluginManager?.getPluginTools() ?? {};

  // Scorers (live evaluation with sampling)
  const outcomeScorer = createOutcomeScorer();
  const efficiencyScorer = createEfficiencyScorer();

  // Notification delivery. Every local Mastra Code process shares one database,
  // so one notification store and one dispatch schedule, and whichever process
  // wins a scheduled fire cannot run another project's thread. When Mastra Code
  // owns the pubsub, each process delivers only the due notifications of the
  // resources its own sessions serve, from its own timer below, and its delivery
  // policy holds every other delivery (see the code agent's `notifications`).
  // The dispatch lease is file-based, so it works with the socket pubsub off
  // too; on Windows there is none, and each process dispatches unleased.
  const ownsNotificationDispatch = !configuredPubSub;
  const dispatchLeasePubSub =
    ownsNotificationDispatch && !ownSignalsPubSub && process.platform !== 'win32'
      ? createSignalsPubSub(project.resourceId)
      : undefined;
  const notificationDispatchLeases = ownsNotificationDispatch
    ? (ownSignalsPubSub ?? dispatchLeasePubSub)?.getLeaseProvider()
    : undefined;
  const notificationDispatcher = createResourceNotificationDispatcher({
    getMastra: () => controller.getMastra(),
    getSessionScopes: async () =>
      (
        await Promise.all(
          [...liveSessions].map(async session => {
            const resourceId = session.identity.getResourceId();
            return (await sessionLeaseScopes(session)).map(scope => ({ resourceId, scope }));
          }),
        )
      ).flat(),
    leases: notificationDispatchLeases,
    owner: `${ownerId}:${process.pid}:${randomUUID()}`,
    onError: error => console.warn('Notification dispatch failed:', error),
  });

  // Agent — githubSignals is created before `controller` but the closure below
  // captures `controller` by reference; it is only invoked at notification time,
  // well after controller is constructed (line ~692). Explicit type annotations
  // on githubSignals, codeAgent, modes, and controller break the circular
  // inference chain this forward reference would otherwise create.

  // Finds the session that runs a wake for `target`. When nobody here owns the
  // resource, a host that resumes sessions (`resumeWakeSession`) brings the
  // owner's session online from its stored row, so the wake runs as that owner
  // rather than as a bare wake with no identity. If it still can't, the wake
  // is refused before any run starts.
  const resolveWakeOwner = async (
    target: { resourceId: string; threadId: string },
    leases?: Parameters<typeof findWakeSession>[3],
  ) => {
    const owner = await findWakeSession(controller, liveSessions, target, leases);
    if (owner || !config?.resumeWakeSession) return owner;
    await config.resumeWakeSession(target);
    const resumed = await findWakeSession(controller, liveSessions, target, leases);
    if (!resumed) {
      throw new Error(`No session owns resource ${target.resourceId} to run its wake on thread ${target.threadId}.`);
    }
    return resumed;
  };

  // Builds the stream options of every wake (a run with no inbound request).
  // Shared by GithubSignals (immediate sends) and the code agent's
  // notification delivery policy (notifications and cross-agent signals,
  // including deferred sends re-dispatched by the core notification dispatch
  // workflow) — all need the target session's request context, or a woken idle
  // thread has no model to run with ("No model selected"). New wake paths must
  // build their options here so `prepareWakeRequestContext` runs for them.
  const getWakeStreamOptions = async ({
    resourceId,
    threadId,
    owner,
  }: {
    resourceId: string;
    threadId: string;
    /** The thread's wake session, when the caller already found it. */
    owner?: WakeSession<MastraCodeState>;
  }) => {
    // Run the woken notification as the session that owns the target thread
    // so it uses that session's model/mode/state/workspace. Never borrow
    // another session: every Mastra Code process shares one database, so the
    // target thread may belong to another project, and running it with this
    // session's workspace would act on the wrong project.
    const wake = owner ?? (await findWakeSession(controller, liveSessions, { resourceId, threadId }));
    // No session here owns the thread. Return undefined so the dispatcher
    // sends a bare wake instead of throwing mid-delivery.
    if (!wake || wake === FOREIGN_THREAD) return undefined;
    const { session } = wake;
    // A long-running system must be able to drive work unattended, so migrate
    // and restore the thread's persisted model before falling back to a real
    // mode or live-session default rather than failing the run.
    const targetThread = await session.thread.getById({ threadId });
    const metadata =
      targetThread?.resourceId === resourceId
        ? (targetThread.metadata as Record<string, unknown> | undefined)
        : undefined;
    const modes = controller.listModes();
    const savedModeId = metadata?.currentModeId;
    const defaultMode = modes.find(mode => mode.default) ?? modes[0];
    const modeId =
      typeof savedModeId === 'string' && modes.some(mode => mode.id === savedModeId)
        ? savedModeId
        : (defaultMode?.id ?? session.mode.get());
    const persistedModelId = metadata
      ? await migratePersistedModelSelection({
          getMetadata: async () =>
            ((await session.thread.getById({ threadId }))?.metadata as Record<string, unknown> | undefined) ?? {},
          modeId,
          set: (key: string, value: unknown) => session.thread.setSettingOn({ threadId, key, value }),
          threadId,
          validModeIds: modes.map(mode => mode.id),
        })
      : undefined;
    const defaultModeModelId = modes.find(mode => mode.id === modeId)?.defaultModelId;
    const modelId = persistedModelId ?? defaultModeModelId ?? session.model.get() ?? '';
    const baseState = { ...session.state.get() } as MastraCodeState;
    delete baseState.modelRoute;
    delete baseState.mastracodePendingModelFallback;
    const persistedSandboxPaths = metadata?.sandboxAllowedPaths;
    baseState.sandboxAllowedPaths =
      Array.isArray(persistedSandboxPaths) && persistedSandboxPaths.every(path => typeof path === 'string')
        ? persistedSandboxPaths
        : [];
    const persistedStateKeys = ['thinkingLevel', 'notifications', 'modelRoute', MODEL_FALLBACK_STATE_KEY] as const;
    for (const key of persistedStateKeys) {
      const value = metadata?.[key];
      if (value !== undefined) (baseState as Record<string, unknown>)[key] = value;
    }
    let notificationState = baseState;
    const getNotificationState = () => ({ ...notificationState });
    const setNotificationState = async (updates: Partial<MastraCodeState>) => {
      notificationState = { ...notificationState, ...updates };
      if (session.thread.getId() === threadId) await session.state.set(updates);
    };
    const updateNotificationState: NonNullable<AgentControllerRequestContext['updateState']> = async updater => {
      const update = await updater(getNotificationState());
      if (update.updates) await setNotificationState(update.updates);
      for (const event of update.events ?? []) {
        if (session.thread.getId() === threadId) session.emit(event);
      }
      return update.result;
    };
    const requestContext = new RequestContext();
    const agentControllerContext: AgentControllerRequestContext = {
      controllerId: controller.id,
      state: getNotificationState(),
      getState: getNotificationState,
      setState: setNotificationState,
      updateState: updateNotificationState,
      getThreadSetting: key => session.thread.getSettingOn({ threadId, key }),
      setThreadSetting: setting => session.thread.setSettingOn({ threadId, key: setting.key, value: setting.value }),
      isThreadActive: () => session.thread.getId() === threadId,
      threadId,
      resourceId,
      sessionId: session.identity.getId(),
      session: {
        id: session.identity.getId(),
        ownerId: session.identity.getOwnerId(),
        modeId,
        modelId,
        state: {
          get: getNotificationState,
          set: setNotificationState,
          update: updateNotificationState,
        },
      },
      workspace: session.getWorkspace(),
      emitEvent: event => {
        if (session.thread.getId() === threadId) session.emit(event);
      },
      getSubagentModelId: params => {
        const agentType = params?.agentType;
        const perType = agentType ? metadata?.[`subagentModelId_${agentType}`] : undefined;
        const global = metadata?.subagentModelId;
        if (typeof perType === 'string') return perType;
        if (typeof global === 'string') return global;
        return session.thread.getId() === threadId ? session.subagents.model.get(params ?? {}) : null;
      },
    };
    requestContext.set('controller', agentControllerContext);
    await config?.prepareWakeRequestContext?.({ requestContext, resourceId, threadId });

    return {
      memory: { thread: threadId, resource: resourceId },
      requestContext,
      maxSteps: 1000,
      savePerStep: false,
      requireToolApproval: notificationState.yolo !== true,
      modelSettings: { temperature: 1 },
    };
  };

  // `/schedules` lives in this process only: timers fire it and it dies with
  // the process, so other Mastra Code processes sharing the database never
  // see or fire it. File-backed schedules are assembled at fire time so script
  // output and file edits are current; scripts run via execFile on the literal
  // path with the workspace as cwd — the extra prompt never reaches a command
  // line. Idle wakes use the shared wake stream options so a model is
  // selected for the woken run (see getWakeStreamOptions).
  const threadScheduler = new ThreadScheduler({
    assemblePrompt: schedule =>
      assembleSchedulePrompt(schedule, {
        cwd: project.rootPath,
        runScript,
        readFile: absPath => readFile(absPath, 'utf8'),
      }),
    deliver: async (schedule, assembled) => {
      const target = { resourceId: schedule.resourceId, threadId: schedule.threadId };
      const owner = await resolveWakeOwner(target);
      const streamOptions =
        owner && owner !== FOREIGN_THREAD ? await getWakeStreamOptions({ ...target, owner }) : undefined;
      // A busy thread gets the prompt as the running agent's next input
      // (`deliver`); `persist` would only write it to history without a run.
      const accepted = await codeAgent.sendSignal(
        {
          type: 'user',
          tagName: 'user',
          contents: assembled.prompt,
          attributes: scheduleSignalAttributes(schedule, assembled),
        },
        {
          ...target,
          ifActive: { behavior: 'deliver' },
          ifIdle: {
            behavior: 'wake',
            ...(streamOptions ? { streamOptions } : {}),
            // Another process owns this thread: hand the wake to it, or refuse
            // it, rather than run it here with no owner identity.
            ...(owner === FOREIGN_THREAD ? { requireClaimedOwner: true } : {}),
          },
        },
      ).accepted;
      if (accepted.action === 'blocked' || accepted.action === 'discard') {
        throw new Error(`the agent did not accept the prompt (${accepted.action})`);
      }
    },
    onError: (error, schedule) => {
      console.warn(`Schedule ${shortScheduleId(schedule.id)} failed to fire:`, error);
    },
  });
  const scheduleTools = useScheduleTools
    ? createScheduleTools({
        scheduler: threadScheduler,
        fileOptions: () => ({
          cwd: project.rootPath,
          homeDir: homedir(),
          fileExists: absPath => {
            try {
              return statSync(absPath).isFile();
            } catch {
              return false;
            }
          },
        }),
      })
    : undefined;

  const githubSignals: GithubSignals | undefined =
    globalSettings.signals?.experimentalGithubSignals && !config?.disableGithubSignals
      ? new GithubSignals({
          cwd: project.rootPath,
          pollIntervalMs: globalSettings.signals.githubPollIntervalMs,
          gitcrawlCommand:
            process.env.MASTRACODE_GITCRAWL_BIN ??
            process.env.GITCRAWL_BIN ??
            process.env.MASTRACODE_GITCRAWL_COMMAND ??
            process.env.GITCRAWL_COMMAND,
          getNotificationStreamOptions: getWakeStreamOptions,
        })
      : undefined;
  // Mastra Code's own processors are constructed once, here, rather than inside
  // the resolver below: the resolver runs before every LLM call, and rebuilding
  // stateful processors per request would reset them.
  const staticConfiguredInputProcessors = Array.isArray(config?.inputProcessors) ? config.inputProcessors : [];
  const mastraCodeInputProcessors: InputProcessor[] = [
    ...staticConfiguredInputProcessors,
    new PlanRejectionAbortProcessor(),
    ...(backgroundToolsEnabled ? [createBackgroundWorkSignalProcessor()] : []),
    new AgentsMDInjector({
      // Untrusted checkouts (review sessions on PR branches) must not have
      // the working tree's instruction files injected as system reminders —
      // those files are attacker-writable content, not configuration. When
      // the session carries a trusted base ref, reminders are served from
      // that ref instead (see getReader); without one they are disabled.
      isEnabled: ({ requestContext }) => {
        const state = getInjectorSessionState(requestContext);
        return state?.untrustedCheckout !== true || typeof state?.baseRef === 'string';
      },
      // Resolve relative tool paths against the session checkout (not the host
      // process cwd) and never surface instruction files from outside it.
      getBasePath: ({ requestContext }) => getInjectorSessionState(requestContext)?.projectPath ?? project.rootPath,
      getReader: ({ requestContext }) => {
        const state = getInjectorSessionState(requestContext);
        if (state?.untrustedCheckout !== true || typeof state?.baseRef !== 'string') return undefined;
        const projectPath = state?.projectPath ?? project.rootPath;
        const [primary] = sessionInstructionRoots(projectPath, state.instructionRoots);
        const reader = createGitRefReminderReader(primary!, state.baseRef);
        if (primary === projectPath) return reader;
        // The base ref covers the session's own checkout only; the other
        // checkouts under the working directory are never read off disk.
        const outsidePrimary = (filePath: string) => {
          const inProject = path.relative(projectPath, filePath);
          const inPrimary = path.relative(primary!, filePath);
          const under = (rel: string) => rel !== '..' && !rel.startsWith(`..${path.sep}`) && !path.isAbsolute(rel);
          return under(inProject) && !under(inPrimary);
        };
        return {
          ...reader,
          pathExists: filePath => !outsidePrimary(filePath) && reader.pathExists(filePath),
          isDirectory: filePath => !outsidePrimary(filePath) && reader.isDirectory(filePath),
        };
      },
      getIgnoredInstructionPaths: ({ requestContext }) => {
        const state = getInjectorSessionState(requestContext);
        const projectPath = state?.projectPath ?? project.rootPath;
        // Same sources, through the same readers, as the static prompt, so the
        // dedup stays consistent on untrusted checkouts and across roots.
        const untrusted = state?.untrustedCheckout === true && typeof state?.baseRef === 'string';
        const roots = sessionInstructionRoots(projectPath, state?.instructionRoots);
        const sources = loadSessionInstructions({
          workingDir: projectPath,
          instructionRoots: state?.instructionRoots,
          untrustedCheckout: untrusted,
          baseRef: untrusted ? state?.baseRef : undefined,
        });
        return instructionSourcePaths(sources, roots);
      },
    }),
  ];

  // Built-in providers are named so the plugin lane can reserve their ids.
  const disabledBuiltinTools = new Set<string>(config?.disableBuiltinTools ?? []);
  const taskSignalProvider: SignalProvider<'task-signals'> = new TaskSignalProvider();
  // The task tools reach the agent through the signal provider rather than the
  // controller built-ins, so disabled ones must be filtered here as well.
  if (disabledBuiltinTools.size > 0) {
    const taskTools = Object.entries(taskSignalProvider.getTools?.() ?? {});
    const enabledTaskTools = Object.fromEntries(taskTools.filter(([id]) => !disabledBuiltinTools.has(id)));
    taskSignalProvider.getTools = () => enabledTaskTools;
  }
  const agentConnectionsSignalProvider = useCrossAgentSignals
    ? new AgentConnectionsSignalProvider(config?.agentConnections)
    : undefined;

  const NO_PLUGIN_PROCESSORS: PluginProcessorEntries = { input: [], output: [] };
  let pluginProcessorReadWarned = false;

  // Providers contributed by plugins are driven from here rather than through
  // the agent's `signals` array: the Agent constructor harvests a provider's
  // processors into a closure it can never undo, so a provider wired there
  // could not be removed when its plugin is disabled, updated or uninstalled.
  // The built-in providers are seeded as reserved ids because they are wired
  // through the constructor and are therefore invisible to the lane.
  const pluginSignalLane = pluginManager
    ? new PluginSignalLane({
        reservedProviderIds: [
          taskSignalProvider.id,
          ...(agentConnectionsSignalProvider ? [agentConnectionsSignalProvider.id] : []),
          ...(githubSignals ? [githubSignals.id] : []),
        ],
      })
    : undefined;
  let unsubscribePluginReload: (() => void) | undefined;

  /**
   * Plugin processors are read through a function so that enabling, disabling or
   * updating a plugin takes effect on the next request rather than requiring a
   * new agent. This runs before every LLM call, and also outside the request
   * path when the Agent catalogues its configured processors — where a throw is
   * swallowed into a debug log. So it only reads already-resolved state: no
   * filesystem, no network, no construction, and it never throws.
   */
  const readPluginProcessors = (): PluginProcessorEntries => {
    try {
      return pluginManager?.getPluginProcessors() ?? NO_PLUGIN_PROCESSORS;
    } catch (error) {
      // Warn once: this is on the hot path, and a broken read repeats.
      if (!pluginProcessorReadWarned) {
        pluginProcessorReadWarned = true;
        console.warn('Failed to read plugin processors:', error);
      }
      return NO_PLUGIN_PROCESSORS;
    }
  };

  const baseCodeAgent: Agent = createCodingAgent({
    id: CODE_AGENT_ID,
    name: 'Code Agent',
    // Workspace is wired per-request at the AgentController level (see
    // `config.workspace` below), so opt out of the factory's default local
    // workspace. An explicit `undefined` is required: the factory only builds a
    // default when the `workspace` key is absent.
    workspace: undefined,
    instructions: async ({ requestContext }) => {
      const configured = config?.hostInstructions;
      const hostInstructions = typeof configured === 'function' ? await configured({ requestContext }) : configured;
      return getDynamicInstructions({
        requestContext,
        hostInstructions,
        coAuthor: config?.coAuthor,
        hasSubconscious,
        hasSubagents: subagents.length > 0,
        disabledBuiltinTools: config?.disableBuiltinTools,
      });
    },
    // `settingsPath` matches the source `createMastraCode()` reads from so the
    // per-mode thinking defaults resolve against the same config file.
    model: withCredentialTenantPreparation(async ctx => {
      // A session's own level wins, so skip the host lookup (and its failure modes) when one is set.
      const controller = ctx.requestContext.get('controller') as
        | AgentControllerRequestContext<{ thinkingLevel?: unknown }>
        | undefined;
      const hasSessionLevel = isThinkingLevelSetting(controller?.state?.thinkingLevel);
      return getDynamicModel(ctx, config?.settingsPath, {
        modeless,
        defaultThinkingLevel: hasSessionLevel ? undefined : await config?.resolveDefaultThinkingLevel?.(ctx),
      });
    }),
    // Deferred notifications are re-dispatched by the core notification
    // dispatch workflow long after the originating send; the delivery policy
    // rebuilds the request context (model selection included) at delivery time
    // so waking an idle thread does not fail with "No model selected". The
    // default decision logic is kept as-is — the policy only attaches
    // streamOptions on top of it.
    notifications: {
      deliveryPolicy: {
        decide: async input => {
          const decision = defaultNotificationDeliveryDecision(input);
          // Without a resourceId there is no session to resolve options from —
          // don't fall through to the active session and wake it under an
          // empty resource binding.
          if (!input.record.resourceId) return decision;
          const target = { resourceId: input.record.resourceId, threadId: input.record.threadId };
          const owner =
            decision.action === 'deliver' || decision.action === 'summarize'
              ? await resolveWakeOwner(target, notificationDispatchLeases)
              : await findWakeSession(controller, liveSessions, target, notificationDispatchLeases);
          // A session here serves the resource but not the thread's project:
          // another process sharing the resource runs it. Leave it pending for
          // that process, whose own dispatch pass for the thread's scope
          // delivers it, rather than running it with this one's workspace.
          if (owner === FOREIGN_THREAD) return { ...decision, hold: true };
          const streamOptions = owner
            ? await getWakeStreamOptions({
                resourceId: input.record.resourceId,
                threadId: input.record.threadId,
                owner,
              })
            : undefined;
          const withOptions = streamOptions ? { ...decision, streamOptions } : decision;
          if (!ownsNotificationDispatch) return withOptions;
          // `hold` only applies at delivery time; it is ignored when a
          // notification is first sent.
          const hold = shouldHoldNotificationDelivery({
            resourceId: input.record.resourceId,
            scope: owner?.scope,
            ownedHere: owner !== undefined,
            dispatcher: notificationDispatcher,
          });
          return hold ? { ...withOptions, hold: true } : withOptions;
        },
      },
    },
    tools: createDynamicTools(
      mcpManager,
      config?.extraTools,
      config?.disabledTools,
      storage,
      pluginTools,
      backgroundToolsEnabled,
      scheduleTools,
    ),
    hooks: createToolHooks(hookManager, config?.postToolObserver),
    scorers: {
      outcome: {
        scorer: outcomeScorer,
        sampling: { type: 'none' },
      },
      efficiency: {
        scorer: efficiencyScorer,
        sampling: { type: 'ratio', rate: 0.3 },
      },
    },
    // TaskSignalProvider bundles the task tools + TaskStateProcessor: it merges
    // the tools into the toolset and registers the task state-signal processor,
    // so the task list persists across turns and survives OM truncation.
    signals: [
      taskSignalProvider,
      ...(agentConnectionsSignalProvider ? [agentConnectionsSignalProvider] : []),
      ...(githubSignals ? [githubSignals] : []),
    ],
    // Native goal mechanism: the in-loop goal step judges the thread's active
    // objective each qualifying iteration. The judge model is required for any
    // gating to occur; when unset the goal step is a complete no-op. A6 auto-wires
    // the GoalStateProcessor so the `<current-objective>` signal persists across
    // turns. Per-thread overrides live in the ThreadState `goal` record and win
    // over these defaults.
    goal: {
      // Resolve the judge model through mastracode's gateway (a model-resolver
      // function) so provider credentials are injected; returns undefined when no
      // judge model is configured, keeping the goal step a no-op. Bind the same
      // `settingsPath` used above so the judge model and `maxRuns` come from one
      // config (a custom settings file would otherwise diverge).
      judge: withCredentialTenantPreparation(ctx => getGoalJudgeModel(ctx, config?.settingsPath)),
      maxRuns: globalSettings.models.goalMaxTurns ?? 50,
      maxSteps: 1000,
      prompt: DEFAULT_GOAL_JUDGE_PROMPT,
      // Read-only workspace tools the default goal judge may call to verify the
      // agent's work against the actual filesystem (view, search_content,
      // find_files, file_stat, lsp_inspect) rather than grading prose alone —
      // restoring the original MastraCode judge's verification ability. Resolved
      // per-request from the active workspace (mirrors `judge`).
      tools: getGoalJudgeTools,
    },
    inputProcessors: ({ requestContext }) => {
      const resolveProcessors = (configured: InputProcessor[]) => [
        ...configured,
        ...mastraCodeInputProcessors,
        // Input-lane notice ONLY (no processAPIError — see the class doc): the
        // runner walks input processors first in runProcessAPIError, so an
        // input-lane processAPIError would rotate before transient retries run.
        new AccountStartNoticeProcessor({ credentialStore: authStorage }),
        ...readPluginProcessors().input.map(entry => entry.value),
        ...(pluginSignalLane?.getInputProcessors() ?? []),
      ];
      if (typeof config?.inputProcessors !== 'function') return resolveProcessors([]);
      const configured = config.inputProcessors({ requestContext });
      return configured instanceof Promise ? configured.then(resolveProcessors) : resolveProcessors(configured);
    },
    // Like the input lane, plugin processors sit last — after the layers they
    // customize, before the channel and memory layers the Agent appends.
    outputProcessors: ({ requestContext }) => {
      const resolveProcessors = (configured: OutputProcessor[]) => [
        // Anthropic cyber refusals finish a step instead of throwing, so they are
        // handled here; OpenAI's throw and are handled in the error lane below.
        new CyberRefusalHandler(),
        ...configured,
        ...readPluginProcessors().output.map(entry => entry.value),
        ...(pluginSignalLane?.getOutputProcessors() ?? []),
      ];
      if (typeof config?.outputProcessors !== 'function') return resolveProcessors(config?.outputProcessors ?? []);
      const configured = config.outputProcessors({ requestContext });
      return configured instanceof Promise ? configured.then(resolveProcessors) : resolveProcessors(configured);
    },
    errorProcessors: [
      // ProviderHistoryCompat must run before StreamErrorRetryProcessor: both react to
      // HTTP 400s, but ProviderHistoryCompat repairs the incompatible history (e.g.
      // sanitizing tool-call IDs) before retrying, while StreamErrorRetryProcessor's
      // isBadRequestError matcher retries the identical request. Error processors
      // short-circuit on the first `retry: true`, so a blind retry first would resend
      // the broken history and fail again. It is named here rather than inherited
      // because the Agent appends missing defaults after a caller's list, which would
      // place this after the retry processor.
      // `prefill-error-handler` is deliberately absent: the Agent appends it from the
      // shared stability defaults, which lands in the same position it occupied here.
      new ProviderHistoryCompat(),
      // Same ordering reason: OpenAI surfaces some cyber refusals as retryable
      // 5xx errors, and a blind retry would resend the request without the
      // `continue` nudge and spend the one retry this handler allows.
      new CyberRefusalHandler(),
      new StreamErrorRetryProcessor({
        matchers: [
          { match: isBadRequestError, maxRetries: 1, delayMs: 2000 },
          {
            match: isTransientConnectionError,
            maxRetries: MASTRACODE_TRANSIENT_CONNECTION_MAX_RETRIES,
            delayMs: ({ retryCount }) => getTransientRetryDelay(retryCount),
            onRetry: ({ error, retryCount, delayMs, requestContext }) =>
              emitTransientRetry(error, retryCount, delayMs, requestContext),
          },
          {
            match: isTransientServerError,
            maxRetries: MASTRACODE_TRANSIENT_CONNECTION_MAX_RETRIES,
            delayMs: ({ retryCount }) => getTransientRetryDelay(retryCount),
            onRetry: ({ error, retryCount, delayMs, requestContext }) =>
              emitTransientRetry(error, retryCount, delayMs, requestContext),
          },
        ],
      }),
      // Rotation runs last in the error lane: a transient error reaching here
      // means StreamErrorRetryProcessor already spent its budget, which is the
      // hop condition; quota/auth errors were never transient-matched and
      // rotate immediately.
      new AccountRotationProcessor({
        credentialStore: authStorage,
        // Same budget core enforces (maxProcessorRetries below): past it, core
        // discards retry:true, so the processor no-ops instead of rotating.
        maxProcessorRetries: MASTRACODE_MAX_PROCESSOR_RETRIES,
        settingsPath: config?.settingsPath,
        modeless,
        resolveDefaultThinkingLevel: config?.resolveDefaultThinkingLevel,
      }),
    ],
    // Individual processors have tighter limits; this remains a defensive
    // ceiling for custom/plugin processors that might retry indefinitely.
    maxProcessorRetries: MASTRACODE_MAX_PROCESSOR_RETRIES,
  });
  const codeAgent = wrapExperimentalAgent(baseCodeAgent, experimentalAgent);

  // const defaultSubAgents: Array<AgentControllerSubagent> = [];
  // const defaultSubagents = [exploreSubagent, planSubagent, executeSubagent];

  const planMode = createPlanMode(!disabledBuiltinTools.has('submit_plan'));
  const defaultModes: AgentControllerMode[] = [
    {
      ...buildMode,
      metadata: {
        ...buildMode.metadata,
        color: mastraBrand.green,
      },
    },
    {
      ...planMode,
      metadata: {
        ...planMode.metadata,
        color: mastraBrand.purple,
      },
    },
    {
      ...fastMode,
      metadata: {
        ...fastMode.metadata,
        color: mastraBrand.orange,
      },
    },
  ];

  const defaultIntervalHandlers: IntervalHandler[] = [
    {
      id: 'gateway-sync',
      intervalMs: 5 * 60 * 1000,
      immediate: false,
      handler: () => syncGateways(),
    },
  ];
  const intervalHandlers = config?.intervalHandlers ?? defaultIntervalHandlers;

  // Build lightweight provider access for resolving built-in packs at startup.
  // Anthropic/OpenAI use AuthStorage; other providers use env API keys.
  // Also scan the full provider registry so configured API keys satisfy access checks.
  const anthropicCred = authStorage.get('anthropic');
  const openaiCred = authStorage.get('openai-codex');
  const githubCopilotCred = authStorage.get('github-copilot');
  const kimiCodingCred = authStorage.get('kimi-for-coding');
  const startupAccess: ProviderAccess = {
    anthropic:
      anthropicCred?.type === 'oauth'
        ? 'oauth'
        : anthropicCred?.type === 'api_key' && anthropicCred.key.trim().length > 0
          ? 'apikey'
          : false,
    openai:
      openaiCred?.type === 'oauth'
        ? 'oauth'
        : openaiCred?.type === 'api_key' && openaiCred.key.trim().length > 0
          ? 'apikey'
          : false,
    cerebras: process.env.CEREBRAS_API_KEY ? 'apikey' : false,
    google: process.env.GOOGLE_GENERATIVE_AI_API_KEY ? 'apikey' : false,
    deepseek: process.env.DEEPSEEK_API_KEY ? 'apikey' : false,
    'github-copilot': githubCopilotCred?.type === 'oauth' ? 'oauth' : false,
    'kimi-for-coding':
      kimiCodingCred?.type === 'oauth' && isKimiCodingDeviceId(kimiCodingCred.deviceId)
        ? 'oauth'
        : (kimiCodingCred?.type === 'api_key' && kimiCodingCred.key.trim().length > 0) ||
            Boolean(process.env.KIMI_API_KEY?.trim())
          ? 'apikey'
          : false,
  };
  // Gateway covers all providers — include Anthropic/OpenAI in OM default selection.
  if (mgApiKey) {
    if (!startupAccess.anthropic) startupAccess.anthropic = 'apikey';
    if (!startupAccess.openai) startupAccess.openai = 'apikey';
  }
  // Check all providers in the registry for API keys
  try {
    const registry = PROVIDER_REGISTRY as Record<string, ProviderConfig>;
    for (const [provider, config] of Object.entries(registry)) {
      if (startupAccess[provider] === 'oauth' || startupAccess[provider] === 'apikey') continue; // Already enabled above
      if (provider === 'anthropic' || provider === 'openai') continue;
      const envVars = config?.apiKeyEnvVar;
      const envVarList = Array.isArray(envVars) ? envVars : envVars ? [envVars] : [];
      if (envVarList.some(envVar => process.env[envVar])) {
        startupAccess[provider] = 'apikey';
      }
    }
  } catch {
    // Registry may not be loaded yet; the 5 hardcoded providers are sufficient fallback
  }
  const builtinOmPacks = getAvailableOmPacks(startupAccess);
  const effectiveDefaults = globalSettings.models.modeDefaults;
  const effectiveObserverModel = resolveOmRoleModel(globalSettings, 'observer', builtinOmPacks);
  const effectiveReflectorModel = resolveOmRoleModel(globalSettings, 'reflector', builtinOmPacks);
  const observerModelSelection = globalSettings.models.observerModelSelection;
  const reflectorModelSelection = globalSettings.models.reflectorModelSelection;
  const effectiveObservationThreshold = globalSettings.models.omObservationThreshold ?? undefined;
  const effectiveReflectionThreshold = globalSettings.models.omReflectionThreshold ?? undefined;
  const effectiveCavemanObservations = globalSettings.models.omCavemanObservations ?? undefined;
  const effectiveObserveAttachments = globalSettings.models.omObserveAttachments ?? 'auto';

  const modes = addPluginToolsToModeAllowlists(
    applyModeDefaultsToModes(
      config?.modes === false ? [buildMode] : (config?.modes ?? defaultModes),
      effectiveDefaults,
    ),
    Object.keys(pluginTools),
  );
  const defaultModeId =
    modes.find(mode => mode.metadata?.default === true)?.id ??
    modes.find(mode => mode.id === 'build')?.id ??
    modes[0]?.id;
  if (!defaultModeId) {
    throw new Error('MastraCode requires at least one mode');
  }

  const subagentModeMap: Record<string, string> = { explore: 'fast', plan: 'plan', execute: 'build' };
  const disabledTools = new Set(config?.disabledTools);
  const subagents = (config?.subagents ?? [exploreSubagent, planSubagent, executeSubagent]).map(definition => ({
    ...definition,
    ...(config?.subagents == null && {
      defaultModelId:
        modes.find(mode => mode.id === subagentModeMap[definition.id])?.defaultModelId ??
        modes.find(mode => mode.id === defaultModeId)?.defaultModelId,
    }),
    allowedControllerTools: definition.allowedControllerTools?.filter(name => !disabledTools.has(name)),
    allowedWorkspaceTools: definition.allowedWorkspaceTools?.filter(name => !disabledTools.has(name)),
    ...(definition.tools && {
      tools: Object.fromEntries(Object.entries(definition.tools).filter(([name]) => !disabledTools.has(name))),
    }),
  }));

  // Build initial state with global preferences. OM knobs are skipped when the
  // host persists memory settings elsewhere (`disableSettingsOmSeed`) so the
  // machine-local settings.json never leaks into server sessions.
  const globalInitialState: Partial<MastraCodeState> = {};
  if (!config?.disableSettingsOmSeed) {
    if (observerModelSelection === 'auto') {
      globalInitialState.observerModelSelection = 'auto';
    } else if (effectiveObserverModel) {
      globalInitialState.observerModelId = effectiveObserverModel;
      globalInitialState.observerModelSelection = effectiveObserverModel;
    } else {
      globalInitialState.observerModelSelection = 'auto';
    }
    if (reflectorModelSelection === 'auto') {
      globalInitialState.reflectorModelSelection = 'auto';
    } else if (effectiveReflectorModel) {
      globalInitialState.reflectorModelId = effectiveReflectorModel;
      globalInitialState.reflectorModelSelection = effectiveReflectorModel;
    } else {
      globalInitialState.reflectorModelSelection = 'auto';
    }
    if (effectiveObservationThreshold !== undefined) {
      globalInitialState.observationThreshold = effectiveObservationThreshold;
    }
    if (effectiveReflectionThreshold !== undefined) {
      globalInitialState.reflectionThreshold = effectiveReflectionThreshold;
    }
    if (effectiveCavemanObservations !== undefined) {
      globalInitialState.cavemanObservations = effectiveCavemanObservations;
    }
    if (effectiveObserveAttachments !== undefined) {
      globalInitialState.observeAttachments = effectiveObserveAttachments;
    }
  }
  if (globalSettings.preferences.yolo !== null) {
    globalInitialState.yolo = globalSettings.preferences.yolo;
  }
  // Note: `thinkingLevel` is intentionally NOT seeded into session state. The
  // state slot is a session-level override; the effective level is resolved at
  // request time (per-mode defaults → global preference) in getDynamicModel so
  // settings changes apply to the next request of every session.
  if (config?.omScope) {
    globalInitialState.omScope = config.omScope;
  }
  // Seed subagent models from global settings
  for (const [key, modelId] of Object.entries(globalSettings.models.subagentModels)) {
    if (key === 'default' || key === '_default') {
      globalInitialState.subagentModelId = modelId;
    } else {
      globalInitialState[`subagentModelId_${key}`] = modelId;
    }
  }

  const typedStateSchema = stateSchema as PublicSchema<MastraCodeState>;
  const backgroundCompletionEvents = backgroundToolsEnabled ? createBackgroundCompletionEvents() : undefined;
  let controller: AgentController<MastraCodeState>;
  controller = new AgentController<MastraCodeState>({
    id: 'mastra-code',
    resourceId: project.resourceId,
    storage,
    ...(backgroundToolsEnabled
      ? {
          backgroundTasks: {
            enabled: true,
            recoverStaleTasksOnStart: false,
            ...createBackgroundCompletionCallbacks(() => sessionRouter, backgroundCompletionEvents),
          },
        }
      : {}),
    observability,
    memory,
    pubsub: signalsPubSub,
    stateSchema: typedStateSchema,
    omConfig: {
      observerModel: 'auto',
      defaultObserverModelId: DEFAULT_OM_MODEL_ID,
      reflectorModel: 'auto',
      defaultReflectorModelId: DEFAULT_OM_MODEL_ID,
      resolveAutoModelId: ({ currentModelId }) => resolveAutoOMModelId(currentModelId, canUseProvider),
    },
    agent: codeAgent,
    subagents,
    ...(config?.disableBuiltinTools?.length ? { disableBuiltinTools: config.disableBuiltinTools } : {}),
    // Subagents resolve like the main agent: tenant credentials and
    // request-scoped custom providers come from the calling run's context.
    // Ids the model router would hand to another registered gateway (by
    // prefix or `handlesModel`) stay strings so that gateway resolves them.
    resolveSubagentModel: (modelId, { requestContext }) =>
      routesToOtherGateway(
        modelId,
        Object.values(controller.getMastra()?.listGateways() ?? {}),
        new Set([mastraCodeGateway.id, amazonBedrockGateway.id, githubCopilotGateway.id, 'models.dev']),
      )
        ? modelId
        : resolveModel(modelId, { requestContext }),
    gateways: [amazonBedrockGateway, githubCopilotGateway, mastraCodeGateway],
    workspace: config?.workspace ?? (args => getDynamicWorkspace({ ...args, backgroundToolsEnabled })),
    browser: config?.browser,
    idGenerator: config?.idGenerator,
    toolCategoryResolver: getToolCategory,
    initialState: {
      projectPath: project.rootPath,
      projectName: project.name,
      gitBranch: project.gitBranch,
      pluginSkillPaths: loadedPlugins.flatMap(plugin => (plugin.status === 'active' ? (plugin.skillPaths ?? []) : [])),
      pluginCommandPaths: loadedPlugins.flatMap(plugin =>
        plugin.status === 'active' ? (plugin.commandPaths ?? []) : [],
      ),
      pluginInstructions: loadedPlugins.flatMap(plugin =>
        plugin.status === 'active' && plugin.instructions ? [plugin.instructions] : [],
      ),
      yolo: true,
      ...globalInitialState,
      ...config?.initialState,
      // configDir must always win over initialState spreads to stay in sync
      // with MCP/hooks/storage which were already initialized with this value.
      configDir,
    },
    modes,
    intervalHandlers,
    modelUseCountProvider: () => loadSettings().modelUseCounts,
    modelUseCountTracker: modelId => {
      try {
        const settings = loadSettings();
        settings.modelUseCounts[modelId] = (settings.modelUseCounts[modelId] ?? 0) + 1;
        saveSettings(settings);
      } catch (error) {
        console.error('Failed to persist model usage count', error);
      }
    },
    threadLock: crossProcessPubSub
      ? undefined
      : {
          acquire: acquireThreadLock,
          release: releaseThreadLock,
        },
  });

  controller.onSessionCreated(session => {
    liveSessions.add(session);
  });
  controller.onSessionDeleted(session => {
    liveSessions.delete(session);
  });

  const sessionPeerCleanup = new WeakMap<Session<MastraCodeState>, () => void>();
  // Thread ownership advertisement is part of experimental cross-agent
  // communication: without it, sessions never claim or advertise their thread to
  // peers. Every thread a session has loaded stays claimed (see
  // `createSessionThreadAdvertisement`) so peers that saved it stay connected
  // after the user moves to another thread.
  if (useCrossAgentSignals) {
    controller.onSessionCreated(
      async session => {
        const advertisement = createSessionThreadAdvertisement({
          session,
          controller,
          projectName: project.name,
        });
        sessionPeerCleanup.set(session, () => advertisement.close());
        const initialThreadId = session.thread.getId();
        if (initialThreadId) {
          // This listener blocks session creation, so bound the initial claim:
          // an unsettled PubSub subscription must not hang createSession().
          // The claim keeps settling in the background either way.
          await Promise.race([
            advertisement.claim(initialThreadId),
            new Promise<void>(resolve => {
              const timer = setTimeout(resolve, 5_000);
              timer.unref?.();
            }),
          ]);
        }
      },
      { blocking: true },
    );
    controller.onSessionDeleted(session => {
      sessionPeerCleanup.get(session)?.();
      sessionPeerCleanup.delete(session);
    });
  }

  // Publish the controller to the plugin runtime accessors now that it exists.
  pluginRuntimeController = controller;

  if (pluginSignalLane && pluginManager) {
    // Register the plugins loaded at startup, and re-reconcile on every reload.
    // Providers are not started here: they need a Mastra instance for storage,
    // and Mastra does not exist until the composition layer boots the controller
    // (see `startPluginSignalProviders` on the returned object).
    pluginSignalLane.sync(pluginManager.getPluginSignalProviders());
    unsubscribePluginReload = pluginManager.onReload(() =>
      pluginSignalLane.sync(pluginManager.getPluginSignalProviders()),
    );
  }

  // The AgentController is fully constructed but intentionally NOT inited here. Init and
  // session creation are deferred to the composition layer (see below) so the
  // controller can be wired in three ways:
  //
  //   1. Server + Web   — registered on a server Mastra, then inited; sessions
  //                       minted per browser client over HTTP.
  //   2. Server + TUI   — same server composition; the TUI drives a session
  //                       (in-process today; remote transport is future work).
  //   3. Local  + TUI   — controller builds its own internal Mastra on init() and
  //                       mints one eager session for the whole process.
  //
  // Cases 1 & 2 use `mountAgentControllerOnMastra` (register-before-init, no eager
  // session). Case 3 uses `bootLocalAgentController` (init + one wired session).
  return {
    controller: controller,
    storage,
    storageMaintenance,
    createKnowledgeInspector: (session: Session<MastraCodeState>, knowledgeKey?: string) =>
      createScopedKnowledgeInspector({
        storage,
        knowledge: knowledgeKey ?? knowledge,
        mastra: controller.getMastra(),
        session,
      }),
    observability,
    memory,
    knowledge,
    knowledgeKey,
    mcpManager,
    hookManager,
    pluginManager,
    loadedPlugins,
    pluginTools,
    signalsPubSub,
    authStorage,
    resolveModel,
    storageWarning,
    observabilityWarning,
    builtinOmPacks,
    effectiveDefaults,
    githubSignals,
    backgroundToolsEnabled,
    backgroundCompletionEvents,
    // Identity for the single local session (Case 3). Servers ignore these and
    // mint per-request sessions with client-supplied resourceIds instead.
    sessionId,
    ownerId,
    // Surface the project root so boot/mount paths can wire workflow tools
    // against a workspace anchored at it without re-running detectProject().
    projectPath: project.rootPath,
    // Surface the Agent instance so registerWorkflowBuilderPrimitives can add
    // it as a plain agent on the Mastra registry. Workflows then compose it
    // as an agent step (agentId: 'code-agent') and delegate open-ended tool
    // orchestration to it — code-agent already has full workspace / MCP / web
    // access via its dynamic tool factory.
    codeAgent,
    // Lets the composition layer publish the created session back into the
    // config closures (e.g. notification stream options read it lazily).
    setActiveSession: (session: Session<MastraCodeState>) => {
      activeSession = session;
    },
    /**
     * Starts the signal providers contributed by plugins. Called by the
     * composition layer once the controller is inited, because that is when a
     * Mastra instance exists — a provider without one has no storage, and
     * nothing else will hand it one: the Agent propagates Mastra only to the
     * providers in its own `signals` array, which these deliberately are not in.
     */
    startPluginSignalProviders: () => {
      const mastra = controller.getMastra();
      if (!pluginSignalLane || !mastra) return;
      pluginSignalLane.setMastra(mastra, codeAgent);
    },
    /**
     * Stops every plugin-contributed signal provider and stops listening for
     * plugin reloads. The inverse of `startPluginSignalProviders`, for an
     * embedder that is done with this controller: a `pluginManager` shared
     * across controllers (`MastraCodeConfig.pluginManager`) outlives any one of
     * them, so without this its providers keep polling and its reload listener
     * keeps firing for a controller that is gone.
     */
    stopPluginSignalProviders: () => {
      unsubscribePluginReload?.();
      unsubscribePluginReload = undefined;
      pluginSignalLane?.stopAll();
    },
    /** Process-local `/schedules` scheduler. Call `stop()` on shutdown. */
    threadScheduler,
    /**
     * Starts delivering the due notifications of this controller's sessions'
     * resources from this process. Called by the composition layer once the
     * controller is inited. A no-op when the embedder configured its own
     * PubSub, whose shared dispatch schedule keeps delivering everything.
     */
    startNotificationDispatch: () => {
      if (ownsNotificationDispatch) notificationDispatcher.start();
    },
    /**
     * Stops advertising every live session's threads and releases their
     * ownership claims. Call first on shutdown: a claim otherwise stays held
     * (and renewed) through the rest of teardown, so a restarted process cannot
     * claim the thread — and peers cannot reach it — until this one exits.
     */
    releaseThreadClaims: () => {
      for (const session of liveSessions) {
        sessionPeerCleanup.get(session)?.();
        sessionPeerCleanup.delete(session);
      }
    },
    /** Stops this process's notification dispatch and releases its leases. Call on shutdown. */
    stopNotificationDispatch: async () => {
      await notificationDispatcher.stop();
      await dispatchLeasePubSub?.close();
    },
    /**
     * Hands Mastra to the statically configured input processors.
     *
     * The Agent does this itself, but only for processors configured as a
     * plain array (`Array.isArray` in `__registerMastra`). This lane is a
     * function so plugins can contribute to it, which takes those processors
     * out of that branch — including any an embedder passed as
     * `config.inputProcessors`, some of which need Mastra to work at all
     * (`CostGuardProcessor` reads observability storage there). Doing it here
     * keeps that unchanged.
     *
     * Plugin processors are deliberately not included: they come and go with
     * their plugin, and the registry keeps the first instance registered under
     * an id forever, which would leave a retired instance behind. Plugins
     * reach Mastra through `getController()` on the plugin context instead.
     */
    registerConfiguredProcessorsWithMastra: () => {
      const mastra = controller.getMastra();
      if (!mastra) return;
      for (const processor of mastraCodeInputProcessors) {
        mastra.addProcessor(processor as Processor);
        mastra.addProcessorConfiguration(processor as Processor, CODE_AGENT_ID, 'input');
      }
    },
  };
}

/**
 * Result of {@link createMastraCodeAgentController}: every shared resource plus the
 * inert AgentController, ready to be either booted locally or mounted on a server
 * Mastra.
 */
export type MastraCodeAgentController = Awaited<ReturnType<typeof createMastraCodeAgentController>>;

/**
 * Wires the session-scoped concerns MastraCode layers on top of a Session:
 * hookManager thread-id sync, GitHub PR polling for the current thread, and
 * per-thread persistence of the mastracode-only `/om` settings.
 *
 * Used by {@link bootLocalAgentController} for the single local session. A server can
 * call this for any session it mints if it wants the same background wiring.
 */
export async function wireSessionConcerns(
  base: Pick<MastraCodeAgentController, 'hookManager' | 'githubSignals' | 'setActiveSession'>,
  session: Session<MastraCodeState>,
): Promise<void> {
  const { hookManager, githubSignals } = base;
  base.setActiveSession(session);

  // Sync hookManager session ID on thread changes
  if (hookManager) {
    session.subscribe((event: AgentControllerEvent) => {
      if (event.type === 'thread_changed') {
        hookManager.setSessionId(event.threadId);
      } else if (event.type === 'thread_created') {
        hookManager.setSessionId(event.thread.id);
      }
    });
  }

  if (githubSignals) {
    const startGithubPollingForCurrentThread = async (threadId?: string | null) => {
      if (!threadId) return;
      githubSignals.stopAllPolling();
      try {
        const threads = await session.thread.list({ allResources: true });
        const thread = threads.find((item: { id: string }) => item.id === threadId);
        await githubSignals.startPollingForThread(
          {
            threadId,
            resourceId: thread?.resourceId ?? session.identity.getResourceId(),
          },
          { pollImmediately: true },
        );
      } catch (error) {
        console.warn('Failed to start GitHub PR polling:', error);
      }
    };

    session.subscribe((event: AgentControllerEvent) => {
      if (event.type === 'thread_changed') void startGithubPollingForCurrentThread(event.threadId);
      else if (event.type === 'thread_created') void startGithubPollingForCurrentThread(event.thread.id);
    });
    void startGithubPollingForCurrentThread(session.thread.getId());
  }

  // Persist MastraCode-owned /om settings per-thread (mastracode-only concern;
  // intentionally not in core's controller loadThreadMetadata).
  const omThreadStateSession = session as unknown as Session<Record<string, unknown>>;
  attachOMThreadStatePersistence(omThreadStateSession);
  await restoreOMThreadStateForCurrentThread(omThreadStateSession).catch(() => {
    // Persistence is best-effort; don't crash startup if storage hiccups.
  });
}

/**
 * Case 3 (AgentController local + TUI/headless): build the controller, let it stand up its
 * own internal Mastra via `init()`, and mint the single eager session that all
 * work in this process runs through. The AgentController owns no session of its own.
 */
export async function bootLocalAgentController(config?: MastraCodeConfig) {
  const base = await createMastraCodeAgentController(config);
  const { controller, sessionId, ownerId, projectPath, codeAgent, mcpManager } = base;

  await controller.init();
  validateExperimentalAgent(codeAgent, controller.getMastra());
  // Register workflow primitives (sub-agent + workspace tools + code-agent
  // + web + notification_inbox + snapshot of MCP tools) on the controller's
  // Mastra so the dynamic-workflow loading in startWorkers() can rehydrate
  // saved workflows against the right tool/agent registry.
  const mastra = controller.getMastra();
  registerSelectedKnowledge(mastra, base.knowledgeKey, base.knowledge);
  if (mastra) await registerWorkflowBuilderPrimitives(mastra, { projectPath, codeAgent, mcpManager });
  await mastra?.startWorkers();
  base.registerConfiguredProcessorsWithMastra();
  base.startPluginSignalProviders();
  base.startNotificationDispatch();
  const session = await controller.createSession({
    id: sessionId,
    ownerId,
    createInitialThread: config?.createInitialThread,
  });
  await wireSessionConcerns(base, session);
  // Knowledge is opt-in: only touch Knowledge storage when a runtime is configured, and never let
  // an incompatible or failing Knowledge store stop the agent from starting.
  let knowledgeInspector: Awaited<ReturnType<typeof base.createKnowledgeInspector>>;
  let knowledgeInspectorUnavailableReason: string | undefined;
  if (!base.knowledge) {
    knowledgeInspectorUnavailableReason =
      'Knowledge is off. Set MASTRACODE_EXPERIMENTAL_SUBCONSCIOUS=1 or pass a `knowledge` instance to createMastraCode.';
  } else {
    try {
      knowledgeInspector = await base.createKnowledgeInspector(session);
      if (!knowledgeInspector) {
        knowledgeInspectorUnavailableReason = 'Knowledge inspection requires a configured knowledge storage domain.';
      }
    } catch (error) {
      knowledgeInspectorUnavailableReason = `Knowledge is unavailable: ${error instanceof Error ? error.message : String(error)}`;
    }
  }

  return {
    ...base,
    session,
    knowledgeInspector,
    knowledgeInspectorUnavailableReason,
  };
}

/**
 * Registers the selected Knowledge on a Mastra built outside `mastraArgs` (the
 * controller's internal Mastra, or a caller-owned one) so keyed lookups resolve
 * it and an instance without its own storage inherits the Mastra's.
 */
function registerSelectedKnowledge(mastra: Mastra | undefined, key: string, knowledge: Knowledge | undefined) {
  if (!mastra || !knowledge) return;
  const existing = (mastra.listKnowledge() as Record<string, Knowledge | undefined>)[key];
  if (existing === knowledge) return;
  if (existing) {
    throw new Error(`This Mastra already registers a different Knowledge instance under "${key}".`);
  }
  mastra.addKnowledge(knowledge, key);
}

/** Result of {@link mountAgentControllerOnMastra}: shared handles plus the owning Mastra. */
export type MountedMastraCode = MastraCodeAgentController & { mastra: Mastra };

/**
 * Cases 1 & 2 (AgentController in Server + Web/TUI): build the controller, register it on a
 * server-owned Mastra, THEN init it. Registering before `init()` is what makes
 * the controller inherit the server's Mastra (storage, agents, gateways) instead of
 * spinning up its own internal one — there is a single shared Mastra.
 *
 * No eager session is minted: each client (browser or terminal) creates/resumes
 * its own isolated session via `controller.createSession({ resourceId })`, so one
 * server can drive many concurrent users.
 *
 * Pass an existing `mastra` to mount onto a Mastra that already hosts other
 * primitives; otherwise a Mastra is created that owns the controller's storage so
 * durability is configured in one place.
 */
export async function mountAgentControllerOnMastra(
  config?: MastraCodeConfig & {
    mastra?: Mastra;
    controllerId?: string;
    buildApiRoutes?: (deps: { controller: MountedMastraCode['controller']; authStorage: AuthStorage }) => ApiRoute[];
    /**
     * Additional `server` config to fold onto the constructed Mastra alongside
     * the assembled `apiRoutes` (e.g. `middleware`, `cors`). Used by the
     * platform entry (`src/mastra/index.ts`) to own the WorkOS gate + tenant
     * dispatcher + CORS on the instance the deployer generates its server from.
     * Ignored when `mastra` is provided (mounting onto a caller-owned instance).
     */
    buildServerConfig?: (deps: {
      controller: MountedMastraCode['controller'];
      authStorage: AuthStorage;
    }) => Omit<NonNullable<ConstructorParameters<typeof Mastra>[0]>['server'], 'apiRoutes'>;
  },
): Promise<MountedMastraCode> {
  const prepared = await prepareAgentControllerMount(config);
  if (config?.mastra) {
    // Mounting onto a Mastra the caller already built. Ensure the controller's
    // back-reference points at it (idempotent — only sets #externalMastra).
    prepared.base.controller.__registerMastra(config.mastra);
    registerSelectedKnowledge(config.mastra, prepared.base.knowledgeKey, prepared.base.knowledge);
    await prepared.finalize();
    return { ...prepared.base, mastra: config.mastra };
  }
  const mastra = new Mastra(prepared.mastraArgs);
  await prepared.finalize();
  return { ...prepared.base, mastra };
}

/**
 * Assemble everything needed to construct the server-owned Mastra WITHOUT
 * constructing it, so a caller (the platform entry `src/mastra/index.ts`) can
 * run the `new Mastra(...)` literal in its own module. The deployer's
 * `checkConfigExport` Babel plugin only marks the config valid when it finds a
 * top-level `new Mastra(...)` exported as `mastra` in the ENTRY file; hiding the
 * construction inside this helper would trip the "Invalid Mastra config" warning.
 *
 * Returns the constructor args plus a `finalize()` that runs the post-construct
 * boot (`controller.init()` + `startWorkers()`). The controller is registered on
 * the Mastra via the `agentControllers` arg at construction time.
 */
export async function prepareAgentControllerMount(
  config?: MastraCodeConfig & {
    mastra?: Mastra;
    controllerId?: string;
    buildApiRoutes?: (deps: { controller: MountedMastraCode['controller']; authStorage: AuthStorage }) => ApiRoute[];
    buildServerConfig?: (deps: {
      controller: MountedMastraCode['controller'];
      authStorage: AuthStorage;
    }) => Omit<NonNullable<ConstructorParameters<typeof Mastra>[0]>['server'], 'apiRoutes'>;
  },
): Promise<{
  base: Awaited<ReturnType<typeof createMastraCodeAgentController>>;
  mastraArgs: NonNullable<ConstructorParameters<typeof Mastra>[0]>;
  finalize: () => Promise<void>;
}> {
  const base = await createMastraCodeAgentController(config);
  const {
    controller,
    storage,
    authStorage,
    projectPath,
    codeAgent,
    mcpManager,
    backgroundToolsEnabled,
    backgroundCompletionEvents,
  } = base;
  const controllerId = config?.controllerId ?? controller.id;
  const apiRoutes = config?.buildApiRoutes?.({ controller, authStorage });
  const extraServerConfig = config?.buildServerConfig?.({ controller, authStorage });
  // Only register workflow primitives when we own the Mastra. If the caller
  // brought their own, they're responsible for what's registered on it.
  const weOwnTheMastra = !config?.mastra;

  const serverConfig = {
    ...extraServerConfig,
    ...(apiRoutes?.length ? { apiRoutes } : {}),
  };
  const mastraArgs = {
    agentControllers: { [controllerId]: controller },
    storage,
    ...(backgroundToolsEnabled
      ? {
          backgroundTasks: {
            enabled: true,
            recoverStaleTasksOnStart: false,
            ...createBackgroundCompletionCallbacks(() => controller, backgroundCompletionEvents),
          },
        }
      : {}),
    ...(base.knowledge ? { knowledge: { [base.knowledgeKey]: base.knowledge } } : {}),
    // Mirror the controller's internal-Mastra construction (which passes
    // `config.pubsub` through): the server-owned Mastra must run its event
    // bus on the same transport so streams/workflows/signals stay
    // cross-process when a distributed PubSub (e.g. Redis Streams) is
    // configured.
    ...(base.signalsPubSub ? { pubsub: base.signalsPubSub } : {}),
    ...(Object.keys(serverConfig).length ? { server: serverConfig } : {}),
  };

  const finalize = async () => {
    await controller.init();
    validateExperimentalAgent(codeAgent, controller.getMastra());
    setCredentialLogger(controller.getMastra()?.getLogger());
    if (weOwnTheMastra) {
      const mastra = controller.getMastra();
      if (mastra) await registerWorkflowBuilderPrimitives(mastra, { projectPath, codeAgent, mcpManager });
    }
    await controller.getMastra()?.startWorkers();
    // Anchored here rather than at a `new Mastra(...)` call site: finalize runs
    // in every mount path (caller-supplied Mastra, SDK-constructed Mastra, and
    // the platform entry that constructs its own), so plugin providers start
    // exactly once regardless of how Mastra Code was mounted.
    base.registerConfiguredProcessorsWithMastra();
    base.startPluginSignalProviders();
    base.startNotificationDispatch();
  };

  return { base, mastraArgs, finalize };
}

/**
 * Back-compat alias. Historically `createMastraCode` built and booted a local
 * controller with a single session; that behavior now lives in
 * {@link bootLocalAgentController}. New code should call the explicit factory for its
 * case: `bootLocalAgentController` (local) or {@link mountAgentControllerOnMastra} (server).
 */
export const createMastraCode = bootLocalAgentController;
export * from './knowledge-inspector.js';
export { LOCAL_KNOWLEDGE_ORG_ID } from './knowledge-scope.js';

/**
 * Programmatic headless API. `runMC` runs an already-built controller/session
 * (from {@link createMastraCode}) as an async-iterable run that also resolves to
 * a typed result. Also available via the `mastracode/headless` subpath.
 */
export {
  runMC,
  runMCCli,
  hasHeadlessFlag,
  autoApprovePolicy,
  denyPolicy,
  permissionModeToPolicy,
  formatHuman,
  formatJsonl,
  renderTextResult,
  renderJsonResult,
} from './headless/index.js';
export type {
  RunMCOptions,
  RunMCResult,
  RunMCStatus,
  RunMCUsage,
  RunMCToolCall,
  RunMCToolResult,
  RunMCError,
  RunMCThreadOptions,
  MCRun,
  ResolutionPolicy,
  PermissionMode,
} from './headless/index.js';
